import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

const EMPTY = {
  date: new Date().toISOString().substring(0,10),
  type: 'Expense', category_id: '', description: '',
  amount_usd: '', amount_lbp: '', currency: 'USD',
  account: '', is_recurring: false, notes: ''
};

export default function Transactions() {
  const [txs, setTxs] = useState([]);
  const [categories, setCategories] = useState([]);
  const today = new Date();
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date(today.getFullYear(), today.getMonth(), 26);
    if (today.getDate() < 26) d.setMonth(d.getMonth() - 1);
    return d.toISOString().substring(0,10);
  });
  const [dateTo, setDateTo] = useState(() => {
    const d = new Date(today.getFullYear(), today.getMonth(), 25);
    if (today.getDate() >= 26) d.setMonth(d.getMonth() + 1);
    return d.toISOString().substring(0,10);
  });
  const [filter, setFilter] = useState({ type:'', category_id:'' });
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lbpRate, setLbpRate] = useState(90000);

  const loadTxs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo, ...filter });
      Object.keys(filter).forEach(k => !filter[k] && params.delete(k));
      const res = await api.get(`/transactions?${params}`);
      setTxs(res.data);
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, filter]);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data));
    api.get('/fx').then(r => {
      const lbp = r.data.find(f=>f.currency==='LBP');
      if (lbp) setLbpRate(Math.round(1/parseFloat(lbp.rate_to_usd)));
    });
  }, []);

  useEffect(() => { loadTxs(); }, [loadTxs]);

  const openAdd = () => { setForm(EMPTY); setEditing(null); setShowForm(true); };
  const openEdit = tx => {
    setForm({
      date: tx.date?.substring(0,10)||'', type:tx.type, category_id:tx.category_id||'',
      description:tx.description||'', amount_usd:tx.amount_usd, amount_lbp:tx.amount_lbp||'',
      currency:tx.currency||'USD', account:tx.account||'', is_recurring:tx.is_recurring,
      notes:tx.notes||''
    });
    setEditing(tx.id); setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.date || !form.type || !form.amount_usd)
      return toast.error('Date, type, and amount required');
    try {
      if (editing) {
        await api.put(`/transactions/${editing}`, form);
        toast.success('Updated');
      } else {
        await api.post('/transactions', form);
        toast.success('Added');
      }
      setShowForm(false); loadTxs();
    } catch (err) { toast.error(err.response?.data?.error||'Error'); }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this transaction?')) return;
    await api.delete(`/transactions/${id}`);
    toast.success('Deleted'); loadTxs();
  };

  const totals = React.useMemo(()=>{
    const t = { Income:0, Expense:0, Savings:0 };
    txs.forEach(tx => t[tx.type] = (t[tx.type]||0) + parseFloat(tx.amount_usd||0));
    return t;
  }, [txs]);

  const inp = s => ({ ...s, padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13, width:'100%', boxSizing:'border-box' });

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:0, fontSize:22, color:'#1E2A38', fontWeight:700 }}>📈 Transactions</h1>
          <p style={{ margin:'4px 0 0', color:'#888', fontSize:13 }}>{txs.length} records</p>
        </div>
        <button onClick={openAdd} style={{
          padding:'9px 18px', borderRadius:8, border:'none',
          background:'#1E2A38', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer'
        }}>+ Add Transaction</button>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:12, color:'#888' }}>From</span>
          <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13 }} />
          <span style={{ fontSize:12, color:'#888' }}>To</span>
          <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13 }} />
        </div>
        <select value={filter.type} onChange={e=>setFilter(f=>({...f,type:e.target.value}))}
          style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13 }}>
          <option value="">All Types</option>
          <option>Income</option><option>Expense</option><option>Savings</option>
        </select>
        <select value={filter.category_id} onChange={e=>setFilter(f=>({...f,category_id:e.target.value}))}
          style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid #ddd', fontSize:13 }}>
          <option value="">All Categories</option>
          {categories.map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
        </select>
      </div>

      {/* Summary strip */}
      <div style={{ display:'flex', gap:12, marginBottom:16 }}>
        {[['Income','#27AE60','💰'],['Expense','#E74C3C','💸'],['Savings','#8E44AD','🏦']].map(([t,c,ico])=>(
          <div key={t} style={{ background:'#fff', borderRadius:8, padding:'10px 16px',
            borderLeft:`3px solid ${c}`, flex:1, boxShadow:'0 1px 4px rgba(0,0,0,0.06)' }}>
            <div style={{ fontSize:11, color:'#888' }}>{ico} {t}</div>
            <div style={{ fontSize:16, fontWeight:700, color:c }}>
              ${Number(totals[t]||0).toLocaleString('en-US',{minimumFractionDigits:2})}
            </div>
          </div>
        ))}
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <button onClick={async()=>{
            try {
              const res = await api.get(`/export/excel?date_from=${dateFrom}&date_to=${dateTo}`, { responseType:'blob' });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement('a'); a.href=url;
              a.download=`Budget_${dateFrom}_${dateTo}.xlsx`; a.click();
              toast.success('Exported');
            } catch(err){ toast.error('Export failed'); }
          }} style={{ padding:'8px 14px', borderRadius:8, border:'1.5px solid #27AE60',
            background:'#fff', color:'#27AE60', fontSize:12, cursor:'pointer', fontWeight:600 }}>
            📥 Excel
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={{ background:'#fff', borderRadius:12, overflow:'hidden', boxShadow:'0 2px 8px rgba(0,0,0,0.07)' }}>
        <table style={{ width:'100%', borderCollapse:'collapse' }}>
          <thead>
            <tr style={{ background:'#1E2A38' }}>
              {['Date','Type','Category','Description','Amount (USD)','Amount (LBP)','Account','Rec.',''].map((h,i)=>(
                <th key={i} style={{ padding:'10px 12px', color:'#fff', fontSize:11, fontWeight:600,
                  textAlign: i>=4?'right':'left', whiteSpace:'nowrap' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ textAlign:'center', padding:40, color:'#aaa' }}>Loading…</td></tr>
            ) : txs.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign:'center', padding:40, color:'#aaa' }}>No transactions found</td></tr>
            ) : txs.map((tx,i)=>{
              const typeColor = tx.type==='Income'?'#27AE60':tx.type==='Savings'?'#8E44AD':'#E74C3C';
              return (
                <tr key={tx.id} style={{ background: i%2===0?'#F8FAFC':'#fff',
                  borderBottom:'1px solid #f0f0f0' }}>
                  <td style={{ padding:'9px 12px', fontSize:12, color:'#555' }}>
                    {new Date(tx.date).toLocaleDateString('en-GB')}
                  </td>
                  <td style={{ padding:'9px 12px' }}>
                    <span style={{ background: typeColor+'22', color: typeColor,
                      borderRadius:12, padding:'2px 8px', fontSize:11, fontWeight:600 }}>
                      {tx.type}
                    </span>
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12 }}>
                    {tx.category_icon} {tx.category_name||'—'}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12, color:'#333' }}>
                    {tx.description||'—'}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12, textAlign:'right', fontWeight:600, color: typeColor }}>
                    ${Number(tx.amount_usd).toLocaleString('en-US',{minimumFractionDigits:2})}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:11, textAlign:'right', color:'#888' }}>
                    {tx.amount_lbp ? `ل.ل ${Number(tx.amount_lbp).toLocaleString()}` : '—'}
                  </td>
                  <td style={{ padding:'9px 12px', fontSize:12, color:'#666' }}>{tx.account||'—'}</td>
                  <td style={{ padding:'9px 12px', fontSize:11, textAlign:'center' }}>
                    {tx.is_recurring ? '🔁' : '—'}
                  </td>
                  <td style={{ padding:'9px 12px' }}>
                    {!tx.month_closed && (
                      <div style={{ display:'flex', gap:6, justifyContent:'flex-end' }}>
                        <button onClick={()=>openEdit(tx)}
                          style={{ padding:'4px 10px', fontSize:11, borderRadius:6, border:'1px solid #ddd',
                            background:'#fff', cursor:'pointer' }}>✏️</button>
                        <button onClick={()=>handleDelete(tx.id)}
                          style={{ padding:'4px 10px', fontSize:11, borderRadius:6, border:'1px solid #fcc',
                            background:'#fff', color:'#E74C3C', cursor:'pointer' }}>🗑️</button>
                      </div>
                    )}
                    {tx.month_closed && <span style={{ fontSize:11, color:'#aaa' }}>🔒</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)',
          display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:540,
            maxWidth:'100%', maxHeight:'90vh', overflowY:'auto' }}>
            <h2 style={{ margin:'0 0 20px', fontSize:17 }}>
              {editing ? '✏️ Edit Transaction' : '+ New Transaction'}
            </h2>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>Date *</label>
                <input type="date" value={form.date} onChange={e=>setForm(f=>({...f,date:e.target.value}))} style={inp({})} />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>Type *</label>
                <select value={form.type} onChange={e=>setForm(f=>({...f,type:e.target.value, category_id:''}))} style={inp({})}>
                  <option>Income</option><option>Expense</option><option>Savings</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>Category</label>
                <select value={form.category_id} onChange={e=>setForm(f=>({...f,category_id:e.target.value}))} style={inp({})}>
                  <option value="">— Select —</option>
                  {categories.filter(c => {
                    if (form.type === 'Savings') return c.name === 'Savings';
                    if (form.type === 'Expense') return c.name !== 'Savings';
                    return true;
                  }).map(c=><option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>Description</label>
                <input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={inp({})} placeholder="e.g. Grocery shopping" />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>Amount USD *</label>
                <input type="number" step="0.01" value={form.amount_usd}
                  onChange={e=>{
                    const v=e.target.value;
                    setForm(f=>({...f,amount_usd:v, amount_lbp:v?Math.round(parseFloat(v)*lbpRate):''}));
                  }} style={inp({})} placeholder="0.00" />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>
                  Amount LBP <span style={{ color:'#aaa', fontWeight:400 }}>(auto-calc)</span>
                </label>
                <input type="number" value={form.amount_lbp}
                  onChange={e=>setForm(f=>({...f,amount_lbp:e.target.value}))} style={inp({})} placeholder="0" />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>Account</label>
                <select value={form.account} onChange={e=>setForm(f=>({...f,account:e.target.value}))} style={inp({})}>
                  <option value="">— Select —</option>
                  {['Cash','Bank','Credit Card','Wallet'].map(a=><option key={a}>{a}</option>)}
                </select>
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:8, paddingTop:20 }}>
                <input type="checkbox" id="rec" checked={form.is_recurring}
                  onChange={e=>setForm(f=>({...f,is_recurring:e.target.checked}))} />
                <label htmlFor="rec" style={{ fontSize:13 }}>🔁 Recurring</label>
              </div>
              <div style={{ gridColumn:'1/-1' }}>
                <label style={{ fontSize:12, fontWeight:600, color:'#555', display:'block', marginBottom:4 }}>Notes</label>
                <textarea value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}
                  rows={2} style={{ ...inp({}), resize:'vertical' }} placeholder="Optional notes…" />
              </div>
            </div>
            <div style={{ display:'flex', gap:12, marginTop:20, justifyContent:'flex-end' }}>
              <button onClick={()=>setShowForm(false)}
                style={{ padding:'9px 18px', borderRadius:8, border:'1.5px solid #ddd', background:'#fff', fontSize:13, cursor:'pointer' }}>
                Cancel
              </button>
              <button onClick={handleSave}
                style={{ padding:'9px 18px', borderRadius:8, border:'none',
                  background:'#1E2A38', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>
                {editing ? 'Save Changes' : 'Add Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
